
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealTimeSubscription } from './useRealTimeSubscription';

interface UseRealtimeProfileUpdatesOptions {
  psychologistId?: string;
  enabled?: boolean;
}

export const useRealtimeProfileUpdates = ({ 
  psychologistId, 
  enabled = true 
}: UseRealtimeProfileUpdatesOptions = {}) => {
  const queryClient = useQueryClient();

  const { isConnected, lastUpdate } = useRealTimeSubscription({
    table: 'psychologists',
    onUpdate: (payload) => {
      console.log('🔄 Real-time profile update received:', payload);
      
      const updatedProfile = payload.new;
      
      if (psychologistId && updatedProfile.id !== psychologistId) {
        return;
      }

      // 1) Atualiza cache individual do perfil
      queryClient.setQueryData(['psychologist-details-debug', updatedProfile.id], updatedProfile);
      queryClient.setQueryData(['psychologist-profile', updatedProfile.id], updatedProfile);
      
      // 2) Atualiza listas onde o psicólogo aparece (carrossel, step1, psychologists page)
      const listQueries = [
        'psychologists-schedule',
        'featured-psychologists-carousel-debug', 
        'public-psychologists-with-availability',
        'admin-psychologist-detail-enhanced',
        'psychologists-list',
        'psychologist-pricing'
      ];

      listQueries.forEach(queryKey => {
        queryClient.setQueryData([queryKey], (oldData: any) => {
          if (!oldData) return oldData;
          
          if (Array.isArray(oldData)) {
            return oldData.map((p: any) => 
              p.id === updatedProfile.id ? { ...p, ...updatedProfile } : p
            );
          }
          
          if (oldData.psychologists && Array.isArray(oldData.psychologists)) {
            return {
              ...oldData,
              psychologists: oldData.psychologists.map((p: any) => 
                p.id === updatedProfile.id ? { ...p, ...updatedProfile } : p
              )
            };
          }
          
          return oldData;
        });
      });

      // 3) Invalida todas as queries para forçar reload
      queryClient.invalidateQueries({ queryKey: ['psychologists-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['featured-psychologists-carousel-debug'] });
      queryClient.invalidateQueries({ queryKey: ['public-psychologists-with-availability'] });

      console.log('✅ Real-time profile update applied to all caches');
    },
    onInsert: (payload) => {
      console.log('🆕 New psychologist created:', payload.new);
      
      queryClient.invalidateQueries({ queryKey: ['psychologists-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['featured-psychologists-carousel-debug'] });
      queryClient.invalidateQueries({ queryKey: ['public-psychologists-with-availability'] });
      queryClient.invalidateQueries({ queryKey: ['psychologists-list'] });
    },
    onDelete: (payload) => {
      console.log('🗑️ Psychologist deleted:', payload.old);
      
      const deletedId = payload.old.id;
      
      queryClient.removeQueries({ queryKey: ['psychologist-details-debug', deletedId] });
      queryClient.removeQueries({ queryKey: ['psychologist-profile', deletedId] });
      
      const listQueries = [
        'psychologists-schedule',
        'featured-psychologists-carousel-debug', 
        'public-psychologists-with-availability',
        'psychologists-list'
      ];

      listQueries.forEach(queryKey => {
        queryClient.setQueryData([queryKey], (oldData: any) => {
          if (!oldData) return oldData;
          
          if (Array.isArray(oldData)) {
            return oldData.filter((p: any) => p.id !== deletedId);
          }
          
          if (oldData.psychologists && Array.isArray(oldData.psychologists)) {
            return {
              ...oldData,
              psychologists: oldData.psychologists.filter((p: any) => p.id !== deletedId)
            };
          }
          
          return oldData;
        });
      });
    }
  });

  return {
    isConnected,
    lastUpdate
  };
};

export const useRealtimeProfileUpdate = (psychologistId: string) => {
  return useRealtimeProfileUpdates({ 
    psychologistId, 
    enabled: !!psychologistId 
  });
};

export const useRealtimeAllProfilesUpdates = () => {
  return useRealtimeProfileUpdates({ enabled: true });
};
